import React from 'react';
import { Box, Button, ButtonGroup, Chip, MenuItem } from '@mui/material';
import { arrayOf, func, shape } from 'prop-types';
import * as R from 'ramda';
import { GridFilterInputValue, useGridApiRef } from '@mui/x-data-grid';
import PersistentDataGrid from 'common/PersistentDataGrid';
import { formatDateForExport } from 'utils/helpers/format';
import { format, parseISO } from 'date-fns';
import MDropdownMenuButton from 'common/MDropdownMenuButton';
import { useUserRolePermissions } from 'hooks';
import palette from 'library/theme/palette';

const AutoReconsignmentCard = ({
  autoReconsignments,
  setConfirmDialogType,
  setIsDialogOpen,
  setSelectedReconsignment,
  setModalMode,
}) => {
  const { isProductOwnerManage, isProductOwnerView } = useUserRolePermissions();

  const formatAutoReconsignments = autoReconsignments.map(item => ({
    ...item,
    isActive: item.isActive ? 'active' : 'inactive',
  }));

  const apiRef = useGridApiRef();

  const handleEditClick = editId => {
    setModalMode('edit');
    setSelectedReconsignment(editId);
  };
  const handleDeleteClick = deleteId => {
    setIsDialogOpen(true);
    setConfirmDialogType('delete');
    setSelectedReconsignment(deleteId);
  };

  const handleViewClick = id => {
    setModalMode('view');
    setSelectedReconsignment(id);
  };

  //  Grid View Implementation of below code
  const minWidth = 150;

  const containsOperator = {
    InputComponent: GridFilterInputValue,
    getApplyFilterFn: filterItem => {
      if (!filterItem.value) return null;
      const filterValue = filterItem.value.trim().toLowerCase();
      return params => params.value?.toString().trim().toLowerCase().startsWith(filterValue);
    },
    label: 'contains',
    value: 'contains',
  };

  const columns = [
    {
      cellClassName: 'css-heap-redact',
      field: 'autoReconsignmentId',
      headerName: 'Auto Recon. ID',
      minWidth,
    },
    {
      cellClassName: 'css-heap-redact',
      field: 'inventoryOwner',
      headerName: 'Inventory Owner',
      minWidth,
    },
    {
      cellClassName: 'css-heap-redact',
      field: 'isActive',
      filterOperators: [containsOperator],
      headerName: 'Active',
      minWidth,
      renderCell: ({ row }) => {
        const status = row.isActive?.toLowerCase();
        if (status === 'active') {
          return (
            <Chip
              key="active"
              label="Active"
              sx={{
                '& > .MuiChip-label': { px: 0 },
                backgroundColor: 'success.light',
                borderRadius: 0.5,
                fontSize: 12,
                height: 20,
                px: 1,
                py: 0.5,
                width: 65,
              }}
            />
          );
        }
        if (status === 'inactive') {
          return (
            <Chip
              key="inactive"
              label="Inactive"
              sx={{
                '& > .MuiChip-label': { px: 0 },
                backgroundColor: 'gray.light',
                borderRadius: 0.5,
                fontSize: 12,
                height: 20,
                px: 1,
                py: 0.5,
                width: 65,
              }}
            />
          );
        }
        return null;
      },
      type: 'string',
    },
    {
      cellClassName: 'css-heap-redact',
      field: 'fromLocation',
      headerName: 'From Location',
      minWidth,
    },
    {
      cellClassName: 'css-heap-redact',
      field: 'toLocation',
      headerName: 'To Location',
      minWidth,
    },
    {
      cellClassName: 'css-heap-redact',
      field: 'product',
      headerName: 'Product',
      minWidth,
    },
    {
      cellClassName: 'css-heap-redact',
      field: 'volume',
      headerName: 'Transfer Vol. (bbls)',
      minWidth,
      type: 'number',
    },
    {
      cellClassName: 'css-heap-redact',
      field: 'thresholdVolume',
      headerName: 'Threshold Vol. (bbls)',
      minWidth,
      type: 'number',
    },
    {
      cellClassName: 'css-heap-redact',
      field: 'lastTriggeredDate',
      headerName: 'Last Triggered Date',
      minWidth,
      renderCell: ({ row }) =>
        R.prop('lastTriggeredDate', row)
          ? format(parseISO(R.prop('lastTriggeredDate', row)), 'MM/dd/yyyy')
          : null,
      valueFormatter: formatDateForExport(),
    },
    {
      cellClassName: 'css-heap-redact',
      field: 'bookedDate',
      headerName: 'Last Booked Date',
      minWidth,
      renderCell: ({ row }) =>
        R.prop('bookedDate', row)
          ? format(parseISO(R.prop('bookedDate', row)), 'MM/dd/yyyy')
          : null,
      valueFormatter: formatDateForExport(),
    },
    {
      cellClassName: 'css-heap-redact',
      field: 'triggerStatus',
      headerName: 'Trigger Status',
      minWidth,
      renderCell: ({ row }) => [
        R.equals('BOOKED', R.prop('triggerStatus', row)) && (
          <Chip
            color="success"
            key="booked"
            label="Booked"
            sx={{
              '& > .MuiChip-label': { px: 0 },
              borderRadius: 0.5,
              fontSize: 12,
              height: 20,
              px: 1,
              py: 0.5,
              width: 65,
            }}
          />
        ),
        R.equals('CANNOT BOOK', R.prop('triggerStatus', row)) && (
          <Chip
            color="error"
            key="cannot-book"
            label="Cannot Book"
            sx={{
              '& > .MuiChip-label': { px: 0 },
              borderRadius: 0.5,
              fontSize: 12,
              height: 20,
              px: 1,
              py: 0.5,
              width: 92,
            }}
          />
        ),
        R.equals('SCHEDULED', R.prop('triggerStatus', row)) && (
          <Chip
            key="scheduled"
            label="Scheduled"
            sx={{
              '& > .MuiChip-label': { px: 0 },
              backgroundColor: palette.success.light,
              borderRadius: 0.5,
              fontSize: 12,
              height: 20,
              maxWidth: 'none',
              px: 1,
              py: 0.5,
              width: 'min-content',
            }}
          />
        ),
        R.equals('EXPIRED', R.prop('triggerStatus', row)) && (
          <Chip
            color="info"
            key="expired"
            label="Expired"
            sx={{
              '& > .MuiChip-label': { px: 0 },
              borderRadius: 0.5,
              fontSize: 12,
              height: 20,
              px: 1,
              py: 0.5,
              width: 65,
            }}
          />
        ),
        R.equals('CANCELED', R.prop('triggerStatus', row)) && (
          <Chip
            color="info"
            key="canceled"
            label="Canceled"
            sx={{
              '& > .MuiChip-label': { px: 0 },
              borderRadius: 0.5,
              fontSize: 12,
              height: 20,
              px: 1,
              py: 0.5,
              width: 75,
            }}
          />
        ),
      ],
    },
    {
      cellClassName: 'css-heap-redact',
      field: 'tooltip',
      headerName: 'Action',
      renderCell: params => {
        if (!isProductOwnerManage && isProductOwnerView) {
          return (
            R.prop('autoReconsignmentId', params.row) && (
              <Button
                onClick={() => handleViewClick(params.row.autoReconsignmentId)}
                sx={{
                  minWidth: 'auto',
                  ml: -0.5,
                  px: 3,
                }}
                variant="outlined"
              >
                View
              </Button>
            )
          );
        }
        if (isProductOwnerManage && isProductOwnerView) {
          return (
            R.prop('autoReconsignmentId', params.row) && (
              <Box>
                <ButtonGroup orientation="horizontal" sx={{ width: '150px' }} variant="outlined">
                  <Button onClick={() => handleEditClick(params.row.autoReconsignmentId)}>
                    Edit
                  </Button>
                  <MDropdownMenuButton>
                    <MenuItem onClick={() => handleDeleteClick(params.row.autoReconsignmentId)}>
                      Delete
                    </MenuItem>
                  </MDropdownMenuButton>
                </ButtonGroup>
              </Box>
            )
          );
        }
        return null;
      },
      width: 360,
    },
  ];

  return (
    <PersistentDataGrid
      {...autoReconsignments}
      apiRef={apiRef}
      cellSelection
      columns={columns}
      getRowId={R.prop('autoReconsignmentId')}
      hideFooter
      id="autoReconsignmentGrid"
      initialState={{
        aggregation: {
          model: {
            thresholdVolume: 'sum',
            volume: 'sum',
          },
        },
      }}
      pageSize={10}
      quickFilter
      rows={formatAutoReconsignments}
      showFilterButton
      slotProps={{
        filterPanel: { sx: { maxWidth: '100vw' } },
      }}
    />
  );
};

AutoReconsignmentCard.propTypes = {
  autoReconsignments: arrayOf(shape({})).isRequired,
  setConfirmDialogType: func.isRequired,
  setIsDialogOpen: func.isRequired,
  setModalMode: func.isRequired,
  setSelectedReconsignment: func.isRequired,
};

export default AutoReconsignmentCard;
